/**
 * Frontend utility for encoding order IDs for public invoice sharing
 * This creates a simple encoding that can be decoded by the backend
 */

/**
 * Encode an order ID for public sharing
 * This creates a simple base64 encoding that the backend can decode
 * @param {string} orderId - The order ID to encode
 * @returns {string} - Encoded string that can be used in public URLs
 */
export function encodeOrderId(orderId) {
  try {
    // Create a payload with timestamp to prevent replay attacks
    const payload = {
      orderId,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    // Convert to JSON and encode as base64
    const payloadString = JSON.stringify(payload);
    const encoded = btoa(payloadString);
    
    // Make it URL-safe
    return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error encoding order ID:', error);
    throw new Error('Failed to encode order ID');
  }
}

/**
 * Generate a public invoice URL with encoded order ID
 * @param {string} orderId - The order ID to encode
 * @param {string} baseUrl - The base URL of the application (optional)
 * @returns {string} - The public invoice URL
 */
const DEFAULT_PUBLIC_INVOICE_BASE =
  'https://trackprofit.in';


export function generatePublicInvoiceUrl(orderId, baseUrl = null) {
  const base = DEFAULT_PUBLIC_INVOICE_BASE;
  const encodedOrderId = encodeOrderId(orderId);
  return `${base}/?inv=${encodedOrderId}`;
}

/**
 * Generate a WhatsApp message with public invoice link
 * @param {Object} invoiceData - The invoice data object
 * @param {string} baseUrl - The base URL of the application (optional)
 * @returns {string} - The formatted WhatsApp message
 */
export function generateWhatsAppMessageWithPublicLink(invoiceData, baseUrl = null, orgData = null) {
  if (!invoiceData.orderId) {
    throw new Error('Order ID is required for public link generation');
  }
  
  const invoiceUrl = generatePublicInvoiceUrl(invoiceData.orderId, baseUrl);
  
  // Limit items to first 3 to avoid message length issues
  const maxItems = 3;
  const items = invoiceData.items || [];
  const displayItems = items.slice(0, maxItems);
  const hasMoreItems = items.length > maxItems;
  
  const hasDiscount = invoiceData.discount && invoiceData.discount > 0;
  
  // Calculate item-level discounts
  const itemsWithDiscounts = displayItems.filter(item => item.discountPercent && item.discountPercent > 0);
  const itemDiscountTotal = itemsWithDiscounts.reduce((sum, item) => {
    const baseTotal = item.price * item.quantity;
    return sum + (baseTotal * item.discountPercent / 100);
  }, 0);
  const overallDiscount = invoiceData.discount - itemDiscountTotal;
  
  // Build items list with discounts
  const itemsList = displayItems.map(item => {
    const baseTotal = item.price * item.quantity;
    const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
    if (itemDiscount > 0) {
      return `• ${item.name}
   ₹${item.price} × ${item.quantity} = ₹${baseTotal.toFixed(2)}
   🎁 Discount ${item.discountPercent}%: -₹${itemDiscount.toFixed(2)}
   ✅ Total: ₹${(baseTotal - itemDiscount).toFixed(2)}`;
    }
    return `• ${item.name} - ₹${item.price} × ${item.quantity} = ₹${baseTotal.toFixed(2)}`;
  }).join('\n\n');
  
  const discountSection = hasDiscount ? `
━━━━━━━━━━━━━━━━━━━━
🎉 *DISCOUNT SUMMARY* 🎉
━━━━━━━━━━━━━━━━━━━━
Subtotal: ₹${(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}
${itemDiscountTotal > 0 ? `Item Discounts: -₹${itemDiscountTotal.toFixed(2)}\n` : ''}${overallDiscount > 0 ? `Overall Discount: -₹${overallDiscount.toFixed(2)}\n` : ''}━━━━━━━━━━━━━━━━━━━━
🎁 *YOU SAVED ₹${invoiceData.discount.toFixed(2)}!* 🎁
━━━━━━━━━━━━━━━━━━━━
` : '';

  return `
📄 *INVOICE: ${invoiceData.billName || 'Invoice'}*

👤 *Customer:* ${invoiceData.customerName}
📞 *Phone:* ${invoiceData.customerPhone}

📋 *Items:*
${itemsList || 'No items'}
${hasMoreItems ? `\n• ... and ${items.length - maxItems} more items` : ''}
${discountSection}
💰 *Total Amount: ₹${invoiceData.total?.toFixed(2) || '0.00'}*

🔗 *View Full Invoice Online:* 
${invoiceUrl}

${orgData?.footerText || invoiceData.footerText || 'Thank you for your business!'} 🙏

Click the link above to view your invoice with full details and payment options.
  `.trim();
}

/**
 * Generate a WhatsApp share URL with public invoice link
 * @param {string} phoneNumber - The customer's phone number
 * @param {Object} invoiceData - The invoice data object
 * @param {string} baseUrl - The base URL of the application (optional)
 * @returns {string} - The WhatsApp share URL
 */
export function generateWhatsAppShareUrlWithPublicLink(phoneNumber, invoiceData, baseUrl = null) {
  const cleanPhone = phoneNumber.replace(/[^\d]/g, '');
  const message = generateWhatsAppMessageWithPublicLink(invoiceData, baseUrl);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
}

/**
 * Test function to verify URL generation
 */
export function testInvoiceEncoding() {
  const testInvoiceData = {
    orderId: 'test-order-123',
    billName: 'INV-20241201-001',
    customerName: 'John Doe',
    customerPhone: '+91 9876543210',
    items: [
      { name: 'Product A', price: 100, quantity: 2 },
      { name: 'Product B', price: 50, quantity: 1 }
    ],
    total: 250
  };

  console.log('Testing Invoice Encoding:');
  console.log('Order ID:', testInvoiceData.orderId);
  console.log('Encoded Order ID:', encodeOrderId(testInvoiceData.orderId));
  console.log('Public Invoice URL:', generatePublicInvoiceUrl(testInvoiceData.orderId));
  console.log('WhatsApp Message:', generateWhatsAppMessageWithPublicLink(testInvoiceData));
  console.log('WhatsApp Share URL:', generateWhatsAppShareUrlWithPublicLink(
    testInvoiceData.customerPhone,
    testInvoiceData
  ));
}

// Export for testing
if (typeof window !== 'undefined') {
  window.testInvoiceEncoding = testInvoiceEncoding;
}